import { memo } from 'react'
import type { Lease, Vm, VmMetrics } from '@/api/types'
import { Checkbox } from '@/components/ui/checkbox'
import { TableCell, TableRow } from '@/components/ui/table'
import { Ahead } from '@/components/Ago'
import { ExpertLine } from '@/components/Expert'
import { IoLine } from '@/components/IoLine'
import { ToneBadge } from '@/components/ToneBadge'
import { AllocationCell, UsageCell } from '@/components/UsageCell'
import { diskAllocatedGb } from '@/domain/lease'
import { ownerLabel } from '@/domain/owner'
import { DASH, gib, minute } from '@/ui/format'
import { badges, ramMb, vmName, type View } from './listRow'
import { VmTags } from './VmTags'

// 관리자 VM 목록(`views/VmList.tsx`)의 한 줄. `memo` 라 받은 것이 모두 이전과 같으면 다시 그리지 않는다 —
// 폴링이 같은 부분을 이전 객체로 두므로(`ui/share.ts`) 사용량이 바뀐 줄과 고르기가 바뀐 줄만 다시 그린다.
// 그래서 받는 것은 응답의 객체(임대·VM·지금 읽기)와 원시값과 고정된 콜백뿐이다. 임대와 VM 을 묶은 `LeaseRow` 는
// 받지 않는다 — 목록이 그릴 때마다 새로 묶는다. 줄을 누르고 고르는 함수는 이 안에서 만든다.

type Props = {
  lease: Lease
  vm: Vm | null
  /** 지금 읽기. 활성 임대가 아니거나 아직 읽지 못했으면 없다. */
  m: VmMetrics | undefined
  /** VM 이 놓인 노드의 이름(고급 보조 줄). 노드 목록이 없는 보는 사람에게는 없다. */
  nodeName: string | undefined
  view: View
  /** 보는 사람이 이 줄을 조작할 수 있는가 — 그런 줄에만 체크박스가 선다. */
  can: boolean
  picked: boolean
  locked: boolean
  /** 줄을 누르면 그 임대의 상세를 연다 — 산 VM 이 있는 줄만이다. */
  onOpen: (leaseId: string) => void
  onPick: (leaseId: string, on: boolean) => void
}

export const VmRow = memo(function VmRow({ lease: l, vm: v, m, nodeName, view, can, picked, locked, onOpen, onPick }: Props) {
  const name = vmName({ lease: l, vm: v })
  return (
    <TableRow
      onClick={() => (v ? onOpen(l.id) : undefined)}
      className={v ? 'cursor-pointer hover:bg-surface-2' : 'hover:bg-transparent'}
    >
      {/* 고를 수 없는 줄의 빈 칸은 좁은 화면에서 접힌다 — 흐르는 줄의 맨 앞에 빈 자리가 남지 않게. */}
      <TableCell className={can ? undefined : 'max-sm:hidden'} onClick={(e) => e.stopPropagation()}>
        {can ? (
          <Checkbox
            aria-label={`${v?.name ?? l.name} 선택`}
            checked={picked}
            disabled={locked}
            onCheckedChange={(on) => onPick(l.id, on === true)}
          />
        ) : null}
      </TableCell>
      <TableCell className="font-medium">
        {ownerLabel(l.owner)}
        {l.owner.name ? <ExpertLine items={[l.owner.username]} className="font-normal" /> : null}
      </TableCell>
      <TableCell className="max-sm:basis-full">
        {name ?? <span className="text-muted-foreground">배정 전</span>}
        <VmTags tags={l.tags} className="mt-0.5 flex" />
        {v ? <ExpertLine items={[`VMID ${v.vmid}`, v.pool ? `풀 ${v.pool}` : null, nodeName]} /> : null}
      </TableCell>
      <TableCell className="max-sm:hidden">
        <UsageCell label="CPU" used={m?.cpu == null ? null : m.cpu * l.cores} total={l.cores} unit="코어" color="var(--chart-1)" />
      </TableCell>
      <TableCell className="max-sm:hidden">
        <UsageCell label="메모리" used={gib(m?.memory_used_mb)} total={gib(ramMb({ lease: l, m }))} unit="size" color="var(--chart-2)" />
      </TableCell>
      <TableCell className="max-sm:hidden">
        <AllocationCell gb={diskAllocatedGb(l)} />
      </TableCell>
      {view === 'retired' ? (
        <>
          <TableCell className="text-[12px] tabular-nums">
            <span className="text-muted-foreground sm:hidden">종료 </span>
            {minute(l.retired_at)}
          </TableCell>
          <TableCell className="text-[12px] tabular-nums">
            <span className="text-muted-foreground sm:hidden">반납 예정 </span>
            {minute(l.purge_after)}
            {l.purge_after ? <div className="text-[11px] text-muted-foreground"><Ahead at={l.purge_after} /></div> : null}
          </TableCell>
        </>
      ) : (
        <>
          <TableCell className="max-sm:hidden">
            <IoLine metrics={m} kind="net" />
          </TableCell>
          <TableCell className="max-sm:hidden">
            <IoLine metrics={m} kind="disk" />
          </TableCell>
        </>
      )}
      <TableCell className="max-sm:hidden">
        <GpuLine lease={l} vm={v} />
      </TableCell>
      <TableCell>
        <div className="flex flex-wrap items-center gap-1.5">
          {badges({ lease: l, vm: v }).map((b, i) => (
            <ToneBadge key={i} tone={b.tone}>{b.word}</ToneBadge>
          ))}
        </div>
      </TableCell>
    </TableRow>
  )
})

/**
 * 받은 GPU — 모델마다 한 줄(`• A16 ×2`). 붙은 카드가 있으면 그 카드이고, 없으면(배정 전·반납됨) 신청한 모델·장 수다.
 * 모델을 고르지 않은 신청은 장 수만 말한다. 글꼴은 네트워크·디스크 칸(`IoLine`)과 같다.
 */
function GpuLine({ lease: l, vm: v }: { lease: Lease; vm: Vm | null }) {
  const counts = new Map<string, number>()
  if (v && v.gpus.length > 0) for (const g of v.gpus) counts.set(g.model ?? 'GPU', (counts.get(g.model ?? 'GPU') ?? 0) + 1)
  else if (l.gpus > 0) counts.set(l.gpu_model ?? `${l.gpus}장`, l.gpu_model ? l.gpus : 1)
  if (counts.size === 0) return <span className="text-[11px] text-muted-foreground">{DASH}</span>
  return (
    <div className="flex flex-col gap-1 text-[11px] tabular-nums text-muted-foreground">
      {[...counts].map(([model, n]) => (
        <span key={model} className="truncate" title={model}>
          • {model}
          {n > 1 ? ` ×${n}` : ''}
        </span>
      ))}
    </div>
  )
}
